// The far future's sky (?whatif=future): episode 6 of the Voidmode Studios
// channel, "The Last Total Eclipse". Only the video renderer drives it
// (window.__future, set every frame from a shot's keys by src/video/
// shotcam.js); without the parameter none of this runs.
//
// moonScale  the Moon's distance as a multiple of today's: the ephemeris
//            position scaled about the Earth's centre, so its direction,
//            phase and eclipses keep their dates and only their size
//            changes (the Moon recedes 3.8 cm a year: 1.08 times today's
//            distance is roughly a billion years on).
// stage      a central solar eclipse placed on purpose: { lat, lon, jd,
//            km } puts the Moon km from the Earth's centre on the line from
//            that place to the Sun at Julian date jd (UTC), moving with the
//            real Moon's velocity of that date (slowed as Kepler's third law
//            has it at the new distance). The shadow's axis then crosses
//            the place at jd, at any hour, so an eclipse of the far future
//            can be shown over a chosen hill with the Sun where the shot
//            wants it. km is the geocentric distance (today's mean 384,400).
// sunScale   the Sun's radius as a multiple of today's, for the eclipse
//            geometry the sky dome draws; from GIANT_FROM up the Sun is a
//            red giant of that size (src/redgiant.js, main.js updateFutureSun).
// orbits     planet names whose orbit lines show (null leaves them alone).
// lights     the Earth's city lights, 1 as today, 0 for nobody there.
// exposure   a fixed exposure for the shot instead of the eye's adaptation
//            to an eclipse (main.js), null to adapt.
// skyFloor   the skylight on the ground under the Moon's shadow, a share of
//            full sunlight (null for the viewer's 0.025). It stands for the
//            light the sunlit air round the shadow scatters in; round a
//            shadow a few kilometres wide that air is itself in a nearly
//            total eclipse, so it sends a hundredth as much.

import { earthRotationAngle, EARTH_OBLIQUITY } from './orbits/earthframe.js';
import { moonGeocentricJ2000, R_EARTH_KM, KM_PER_AU } from './eclipse/geometry.js';
import { getPlanetPosition } from './orbits/ephemeris.js';

export function futureRequested(search) {
  return new URLSearchParams(search || '').get('whatif') === 'future';
}

export const futureState = {
  moonScale: 1,
  stage: null,
  sunScale: 1,
  exposure: null,
  orbits: null,
  lights: null,
  skyFloor: null,
};

// The place's centre-relative position at jd, J2000 ecliptic km, on the
// viewer's sphere (earthframe.js: the mesh turns by the Earth rotation angle
// inside a group tilted by the obliquity).
export function placeEclipticKm(lat, lon, jd, r = R_EARTH_KM) {
  const phi = (lat * Math.PI) / 180;
  const a = (lon * Math.PI) / 180 + earthRotationAngle(jd);
  const xq = Math.cos(phi) * Math.cos(a), yq = Math.cos(phi) * Math.sin(a), zq = Math.sin(phi);
  const e = EARTH_OBLIQUITY;
  return { x: r * xq, y: r * (yq * Math.cos(e) + zq * Math.sin(e)), z: r * (-yq * Math.sin(e) + zq * Math.cos(e)) };
}

// The Sun as the scene draws it from the Earth (J2000 ecliptic km): the
// Earth's ephemeris place at the clock's own date, as main.js puts it, which
// is what the sky dome eclipses. The eclipse geometry's Sun takes Terrestrial
// Time and the Earth's offset from the barycentre, several arcseconds away,
// and a stage aimed at it reached totality seconds late.
export function stagedSunKm(jd) {
  const e = getPlanetPosition('earth', jd);
  return { x: -e.x * KM_PER_AU, y: -e.y * KM_PER_AU, z: -e.z * KM_PER_AU };
}

// v turned by the rotation that takes the direction of p onto that of q
// (Rodrigues' formula about their cross product).
function turned(v, p, q) {
  const pl = Math.hypot(p.x, p.y, p.z), ql = Math.hypot(q.x, q.y, q.z);
  const ax = { x: (p.y * q.z - p.z * q.y) / (pl * ql), y: (p.z * q.x - p.x * q.z) / (pl * ql), z: (p.x * q.y - p.y * q.x) / (pl * ql) };
  const sin = Math.hypot(ax.x, ax.y, ax.z);
  const cos = (p.x * q.x + p.y * q.y + p.z * q.z) / (pl * ql);
  if (sin < 1e-12) return cos > 0 ? v : { x: -v.x, y: -v.y, z: -v.z };
  const n = { x: ax.x / sin, y: ax.y / sin, z: ax.z / sin };
  const nv = n.x * v.x + n.y * v.y + n.z * v.z;
  const c = { x: n.y * v.z - n.z * v.y, y: n.z * v.x - n.x * v.z, z: n.x * v.y - n.y * v.x };
  return {
    x: v.x * cos + c.x * sin + n.x * nv * (1 - cos),
    y: v.y * cos + c.y * sin + n.y * nv * (1 - cos),
    z: v.z * cos + c.z * sin + n.z * nv * (1 - cos),
  };
}

let stageKey = '';
let stageOrbit = null;
function stagedOrbit(stage) {
  const key = `${stage.lat},${stage.lon},${stage.jd},${stage.km}`;
  if (key === stageKey) return stageOrbit;
  const P = placeEclipticKm(stage.lat, stage.lon, stage.jd);
  const S = stagedSunKm(stage.jd);
  const d = { x: S.x - P.x, y: S.y - P.y, z: S.z - P.z };
  const dl = Math.hypot(d.x, d.y, d.z);
  const u = { x: d.x / dl, y: d.y / dl, z: d.z / dl };
  const pu = P.x * u.x + P.y * u.y + P.z * u.z;
  const pp = P.x * P.x + P.y * P.y + P.z * P.z;
  const s = -pu + Math.sqrt(pu * pu - pp + stage.km * stage.km);
  const M0 = { x: P.x + u.x * s, y: P.y + u.y * s, z: P.z + u.z * s };
  // The real Moon's velocity that day, km a day, slowed for the distance,
  // and turned with the Moon from where it really is to the staged place
  // (about the axis square to both): on a date away from new Moon the
  // copied vector would otherwise carry the staged Moon across the Sun
  // the wrong way.
  const h = 0.01;
  const a = moonGeocentricJ2000(stage.jd - h), b = moonGeocentricJ2000(stage.jd + h);
  const m = moonGeocentricJ2000(stage.jd);
  const k = Math.sqrt(m.km / stage.km) / (2 * h);
  const V = turned({ x: (b.x - a.x) * k, y: (b.y - a.y) * k, z: (b.z - a.z) * k }, m, M0);
  stageKey = key;
  stageOrbit = { M0, V, jd: stage.jd };
  return stageOrbit;
}

// The Moon's geocentric position (J2000 ecliptic km, with km its length) as
// the future has it, from the ephemeris' own position p at jd.
export function futureMoonKm(jd, p) {
  const st = futureState.stage;
  if (st && Number.isFinite(st.jd) && Number.isFinite(st.km)) {
    const o = stagedOrbit(st);
    const t = jd - o.jd;
    const x = o.M0.x + o.V.x * t, y = o.M0.y + o.V.y * t, z = o.M0.z + o.V.z * t;
    return { x, y, z, km: Math.hypot(x, y, z) };
  }
  const k = futureState.moonScale > 0 ? futureState.moonScale : 1;
  if (k === 1) return p;
  return { x: p.x * k, y: p.y * k, z: p.z * k, km: p.km * k };
}

export function installFutureHook() {
  if (typeof window === 'undefined') return;
  window.__future = {
    state: futureState,
    set(values) {
      if (!values) return;
      for (const [key, value] of Object.entries(values)) {
        if (key in futureState) futureState[key] = value;
      }
    },
  };
}
